import { cellKey, getTile, isInside } from "./map";
import type { GameSnapshot, InvariantViolation, TileType } from "./types";

function violation(
  code: string,
  message: string,
  path?: string,
): InvariantViolation {
  return { code, message, ...(path === undefined ? {} : { path }) };
}

export function validateSnapshot(
  snapshot: GameSnapshot,
): readonly InvariantViolation[] {
  const errors: InvariantViolation[] = [];
  const map = snapshot.map;

  if (!Number.isInteger(snapshot.tick) || snapshot.tick < 0) {
    errors.push(
      violation("tick.invalid", "tick must be a non-negative integer", "tick"),
    );
  }
  if (map.width < 3 || map.height < 3) {
    errors.push(violation("map.size", "map dimensions are too small", "map"));
  }
  if (map.tiles.length !== map.height) {
    errors.push(
      violation("map.rows", "map row count does not match height", "map.tiles"),
    );
  }

  const playerIds = new Set<string>();
  const occupiedPlayers = new Set<string>();
  for (const player of snapshot.players) {
    if (playerIds.has(player.id)) {
      errors.push(
        violation(
          "player.duplicate",
          `duplicate player ${player.id}`,
          "players",
        ),
      );
    }
    playerIds.add(player.id);
    if (!isInside(map, player.cell)) {
      errors.push(
        violation(
          "player.bounds",
          `player ${player.id} is outside the map`,
          `players.${player.id}`,
        ),
      );
    }
    if (player.alive) {
      const key = cellKey(player.cell);
      if (occupiedPlayers.has(key)) {
        errors.push(
          violation(
            "player.overlap",
            `alive players overlap at ${key}`,
            `players.${player.id}.cell`,
          ),
        );
      }
      occupiedPlayers.add(key);
      if (getTile(map, player.cell) !== "floor") {
        errors.push(
          violation(
            "player.wall",
            `alive player ${player.id} occupies a non-floor tile`,
            `players.${player.id}`,
          ),
        );
      }
    }
    if (player.health < 0 || player.health > 1) {
      errors.push(
        violation(
          "player.health",
          `player ${player.id} has invalid health`,
          `players.${player.id}`,
        ),
      );
    }
    if (
      player.maxBombs < 0 ||
      player.activeBombs < 0 ||
      player.bombsAvailable < 0
    ) {
      errors.push(
        violation(
          "player.inventory",
          `player ${player.id} has invalid bomb inventory`,
          `players.${player.id}`,
        ),
      );
    }
    if (player.bombsAvailable !== player.maxBombs - player.activeBombs) {
      errors.push(
        violation(
          "player.inventory-mismatch",
          `player ${player.id} has inconsistent bomb inventory`,
          `players.${player.id}`,
        ),
      );
    }
  }

  const bombsByCell = new Set<string>();
  const bombsByOwner = new Map<string, number>();
  for (const bomb of snapshot.bombs) {
    if (!isInside(map, bomb.cell)) {
      errors.push(
        violation(
          "bomb.bounds",
          `bomb ${bomb.id} is outside the map`,
          `bombs.${bomb.id}`,
        ),
      );
    }
    if (getTile(map, bomb.cell) !== "floor") {
      errors.push(
        violation(
          "bomb.wall",
          `bomb ${bomb.id} occupies a non-floor tile`,
          `bombs.${bomb.id}`,
        ),
      );
    }
    const key = cellKey(bomb.cell);
    if (bombsByCell.has(key)) {
      errors.push(
        violation(
          "bomb.overlap",
          `multiple bombs occupy ${key}`,
          `bombs.${bomb.id}`,
        ),
      );
    }
    bombsByCell.add(key);
    if (!playerIds.has(bomb.ownerId)) {
      errors.push(
        violation(
          "bomb.owner",
          `bomb ${bomb.id} has no player owner`,
          `bombs.${bomb.id}`,
        ),
      );
    }
    if (bomb.fuseRemaining < 0 || !Number.isInteger(bomb.fuseRemaining)) {
      errors.push(
        violation(
          "bomb.fuse",
          `bomb ${bomb.id} has an invalid fuse`,
          `bombs.${bomb.id}`,
        ),
      );
    }
    bombsByOwner.set(bomb.ownerId, (bombsByOwner.get(bomb.ownerId) ?? 0) + 1);
  }

  for (const player of snapshot.players) {
    const bombOnPlayer = snapshot.bombs.find(
      (bomb) => bomb.cell.x === player.cell.x && bomb.cell.y === player.cell.y,
    );
    if (
      player.alive &&
      bombOnPlayer !== undefined &&
      !player.bombPass &&
      bombOnPlayer.ownerId !== player.id
    ) {
      errors.push(
        violation(
          "player.bomb-overlap",
          `player ${player.id} overlaps a bomb without bomb-pass ability`,
          `players.${player.id}`,
        ),
      );
    }
  }

  for (const player of snapshot.players) {
    const actual = bombsByOwner.get(player.id) ?? 0;
    if (actual !== player.activeBombs) {
      errors.push(
        violation(
          "bomb.count",
          `player ${player.id} reports ${player.activeBombs} active bombs but owns ${actual}`,
          `players.${player.id}`,
        ),
      );
    }
  }

  const itemCells = new Set<string>();
  const itemIds = new Set<string>();
  for (const item of snapshot.items) {
    if (itemIds.has(item.id)) {
      errors.push(
        violation(
          "item.duplicate-id",
          `duplicate item id ${item.id}`,
          `items.${item.id}`,
        ),
      );
    }
    itemIds.add(item.id);
    if (!isInside(map, item.cell)) {
      errors.push(
        violation(
          "item.bounds",
          `item ${item.id} is outside the map`,
          `items.${item.id}`,
        ),
      );
    }
    if (getTile(map, item.cell) !== "floor") {
      errors.push(
        violation(
          "item.wall",
          `item ${item.id} occupies a non-floor tile`,
          `items.${item.id}`,
        ),
      );
    }
    const key = cellKey(item.cell);
    if (itemCells.has(key)) {
      errors.push(
        violation(
          "item.overlap",
          `multiple items occupy ${key}`,
          `items.${item.id}`,
        ),
      );
    }
    itemCells.add(key);
  }

  const flameCells = new Set<string>();
  for (const flame of snapshot.flames) {
    if (!isInside(map, flame.cell)) {
      errors.push(
        violation(
          "flame.bounds",
          `flame is outside the map at ${cellKey(flame.cell)}`,
          "flames",
        ),
      );
    }
    if (!Number.isInteger(flame.remaining) || flame.remaining <= 0) {
      errors.push(
        violation(
          "flame.lifetime",
          `flame at ${cellKey(flame.cell)} has invalid lifetime`,
          "flames",
        ),
      );
    }
    flameCells.add(cellKey(flame.cell));
  }

  const suddenDeathCells = new Set<string>();
  for (const cell of snapshot.suddenDeathHazards) {
    if (!isInside(map, cell)) {
      errors.push(
        violation(
          "sudden-death.bounds",
          `sudden-death cell is outside the map`,
          "suddenDeathHazards",
        ),
      );
    }
    const key = cellKey(cell);
    if (suddenDeathCells.has(key)) {
      errors.push(
        violation(
          "sudden-death.duplicate",
          `duplicate sudden-death cell ${key}`,
          "suddenDeathHazards",
        ),
      );
    }
    suddenDeathCells.add(key);
  }

  if (snapshot.status === "won") {
    if (snapshot.winnerId === undefined || !playerIds.has(snapshot.winnerId)) {
      errors.push(
        violation(
          "round.winner",
          "won round must name an existing winner",
          "winnerId",
        ),
      );
    }
  } else if (snapshot.winnerId !== undefined) {
    errors.push(
      violation(
        "round.winner-unexpected",
        "only a won round may have a winner",
        "winnerId",
      ),
    );
  }

  return errors;
}

export function assertSnapshotInvariants(snapshot: GameSnapshot): void {
  const errors = validateSnapshot(snapshot);
  if (errors.length > 0) {
    throw new Error(
      errors.map((error) => `${error.code}: ${error.message}`).join("; "),
    );
  }
}

export const checkInvariants = validateSnapshot;
export const assertInvariants = assertSnapshotInvariants;

export function isValidSnapshot(snapshot: GameSnapshot): boolean {
  return validateSnapshot(snapshot).length === 0;
}

export function isKnownTile(tile: string): tile is TileType {
  return (
    tile === "floor" || tile === "indestructible" || tile === "destructible"
  );
}
